/**
 * Token utility functions for JWT validation and expiry checking
 */

/**
 * Decode a JWT token without verification (for checking expiry)
 * @param {string} token - JWT token to decode
 * @returns {object|null} - Decoded payload or null if invalid
 */
export function decodeToken(token) {
  try {
    if (!token || typeof token !== 'string') return null;
    
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    
    const payload = parts[1];
    const decoded = JSON.parse(atob(payload));
    return decoded;
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
}

/**
 * Check if a JWT token is expired
 * @param {string} token - JWT token to check
 * @returns {boolean} - True if expired, false if still valid
 */
export function isTokenExpired(token) {
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) return true;
  
  const currentTime = Math.floor(Date.now() / 1000);
  const isExpired = decoded.exp < currentTime;
  
  // Log for debugging
  if (isExpired) {
    const expiredAgo = currentTime - decoded.exp;
    console.log(`Token expired ${expiredAgo} seconds ago`);
  } else {
    const expiresIn = decoded.exp - currentTime;
    console.log(`Token expires in ${expiresIn} seconds (${Math.floor(expiresIn / 60)} minutes)`);
  }
  
  return isExpired;
}

/**
 * Check if a token will expire soon (within threshold seconds)
 * @param {string} token - JWT token to check
 * @param {number} thresholdSeconds - Number of seconds to consider "soon" (default: 300 = 5 minutes)
 * @returns {boolean} - True if expiring soon, false otherwise
 */
export function isTokenExpiringSoon(token, thresholdSeconds = 300) {
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) return true;
  
  const currentTime = Math.floor(Date.now() / 1000);
  const timeUntilExpiry = decoded.exp - currentTime;
  
  return timeUntilExpiry < thresholdSeconds;
}

/**
 * Get token expiry time as a Date object
 * @param {string} token - JWT token
 * @returns {Date|null} - Expiry date or null if invalid
 */
export function getTokenExpiry(token) {
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) return null;
  
  return new Date(decoded.exp * 1000);
}

/**
 * Get time remaining until token expiry in seconds
 * @param {string} token - JWT token
 * @returns {number|null} - Seconds until expiry or null if invalid/expired
 */
export function getTimeUntilExpiry(token) {
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) return null;
  
  const currentTime = Math.floor(Date.now() / 1000);
  const timeRemaining = decoded.exp - currentTime;
  
  return timeRemaining > 0 ? timeRemaining : 0;
}

/**
 * Format time remaining in human-readable format
 * @param {number} seconds - Number of seconds
 * @returns {string} - Formatted string like "5 minutes" or "2 hours"
 */
export function formatTimeRemaining(seconds) {
  if (seconds <= 0) return 'expired';
  
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  
  if (days > 0) return `${days} day${days !== 1 ? 's' : ''}`;
  if (hours > 0) return `${hours} hour${hours !== 1 ? 's' : ''}`;
  if (minutes > 0) return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  return `${seconds} second${seconds !== 1 ? 's' : ''}`;
}
